import { useEffect, useState, useRef } from "react";
import * as speechsdk from "microsoft-cognitiveservices-speech-sdk";
import { AudioConfig } from "microsoft-cognitiveservices-speech-sdk";
import { useAppContext } from "../appContext";
import { getTokenOrRefresh, UpdateAssessmentMicActivationFailure, updateFeedbackAccessibility, logAssessmentEvent  } from "../api";
import Nav from "./nav";
import ResizeFeedback from "./ResizeFeedback";
import FeedbackLoader from "./FeedbackLoader";
import { formatTime } from "@/utils/utils";
import { FeedbackType } from "@/utils/types";
import { feedbackMap, generateGUID } from "@/utils/utils";
import { Visibility } from "@mui/icons-material"; //bruno

function InterviewScreen({ isAdminView }: { isAdminView?: boolean }) {
  const { state, reducers, startFunc, gpt, stopAudio, navigateThenRun, setQs } =
    useAppContext();
  const {
    updatesession,
    setrecognizedsp,
    setprocessing,
    setshowApiError,
    setErrorText,
    setNextFunc,
    setshowNextConfirmation,
    setspeechLen,
    sethitMaxLen,
    setcanStop,
    setfeedbackcontext,
    setrole,
    settype,
    setqType,
    setcurrentQuestion,
    setretryPast,
    setprofileUser,
    setpastInfo,
  } = reducers;

  const [showLoader, setShowLoader] = useState(false);
  const [recognizer, setrecognizer] =
    useState<speechsdk.SpeechRecognizer | null>(null);
  const [startTimer, setStartTimer] = useState<boolean>(false);
  const [sessionStopped, setsessionStopped] = useState<boolean>(false);
  const [micListening, setmicListening] = useState<boolean>(false);
  const [isRetry, setisRetry] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);
  const [rsp, setrsp] = useState<string>(""); // recognizedspeech in interviewscreen
  const [rspLive, setrspLive] = useState<string>("");
    // Fullscreen enforcement state
  const [isFullscreen, setIsFullscreen] = useState<boolean>(() => {
    return !!(document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).msFullscreenElement);
  });
  // Track when fullscreen is exited and duration until re-enabled
  const [fullscreenExitTime, setFullscreenExitTime] = useState<number | null>(null);
  const [fullscreenReentryDuration, setFullscreenReentryDuration] = useState<number | null>(null);

  const [intInterval, setIntInterval] = useState<NodeJS.Timeout | undefined>(
    undefined
  );
  const [updatedAssessmentMicActivationFailure, setUpdatedAssessmentMicActivationFailure] =
    useState<boolean>(false);
  const [micActivationTimeout, setmicActivationTimeout] =
    useState<NodeJS.Timeout | null>(null);

  let audioElement: HTMLAudioElement | null = document.getElementById(
    "audioElement"
  ) as HTMLAudioElement | null;
  let micTimeout: NodeJS.Timeout | null = null;

  const MAXCALLS = 12;
  const token = new URLSearchParams(window.location.search).get('candidate')||"";
  const stopMic = (): void => {
    if (micListening === true) {
      if (micTimeout) {
        clearTimeout(micTimeout);
        micTimeout = null;
      }
      setmicListening(false);
      if (recognizer) {
        // Wait for final recognition before stopping
        setTimeout(() => {
          recognizer.stopContinuousRecognitionAsync();
        }, 500);
      }
      setprocessing(true);
    }
  };

  const sttFromMic = (): void => {
    if (!startTimer) {
      setStartTimer(true);
    }
    setrsp("");
    setmicListening(true);
    setsessionStopped(false);
  };

  useEffect(() => {
    if (isRetry || (state.currentQuestion >= 0 && state.session.length > 0)) {
      if (state.session[state.currentQuestion].feedback === "") {
        synthesizeSpeech(
          state.session[state.currentQuestion].question.question
        );
      }
      setrecognizedsp("");
      setrsp("");
      setisRetry(false);
    }
  }, [state.currentQuestion, isRetry]);

  useEffect(() => {
    if (rsp !== "") {
      let l = state.speechLen + rsp.split(" ").length + 1;
      //console.log(l);
      if (l + state.contextLength > 5000) {
        // 5000 is true max, giving room for gpt 68 words response and also any final words user has.
        sethitMaxLen(true);
      }
      setspeechLen(l);
      setrspLive("");
    }
    setrecognizedsp(rsp);
  }, [rsp]);

  useEffect(() => {
    if (rspLive !== "") {
      let prompt1 = [...state.session[state.currentQuestion].prompt];
      if (prompt1.length > 0 && prompt1[prompt1.length - 1].role == "user") {
        prompt1[prompt1.length - 1] = {
          role: "user",
          content: `${rsp} ${rspLive}`,
        };
      } else {
        prompt1.push({ role: "user", content: `${rsp} ${rspLive}` });
      }
      updatesession({ prompt: prompt1 });
    }
  }, [rspLive]);

  // Useful function
  useEffect(() => {
    if (
      !startTimer &&
      state.session[state.currentQuestion]?.code !== "" &&
      state.session[state.currentQuestion]?.feedback === ""
    ) {
      setStartTimer(true);
    }
  }, [state.session[state.currentQuestion]?.code]);

  // only for interview screen not past feedback
  useEffect(() => {
    if (state.showFeedback) {
      setStartTimer(false);
      if (state.session[state.currentQuestion]?.feedback !== "") {
        setShowLoader(false);
      }
    }
    const fetchFeedback = async () => {
      if (state.session.length > 0 && state.showFeedback) {
        const feed = await feedbackMap(
          state.session[state.currentQuestion].feedback,
          true
        );
        if (!feed) {
          setfeedbackcontext([]);
          return;
        }

        const finalFeedback: FeedbackType = [];
        for (let i = 0; i < feed[0].length; i++) {
          finalFeedback.push({
            title: feed[0][i],
            badgeType: feed[1][i] as any,
            desc: feed[2][i],
          });
        }

        setfeedbackcontext(finalFeedback);
      }
    };
    if (state.showFeedback) {
      fetchFeedback();
    }
  }, [
    state.session[state.currentQuestion]?.feedback,
    state.showFeedback,
    state.currentQuestion,
  ]);

  const updateAccessibleToOrg = async (e: boolean): Promise<void> => {
    const access = state.pastInfo["orgAccessible"];
    if (access === undefined || access !== e) {
      const user2 = await updateFeedbackAccessibility(
        e,
        state.session[state.currentQuestion].question.question,
        state.pastInfo["index"]
      );
      setprofileUser(user2);
      setpastInfo({ ...state.pastInfo, orgAccessible: e });
    }
  };

  const NewQuestionClick = (ind: number) => {
    if (state.isAssessment) return;
    if (rsp === "" || state.session[state.currentQuestion].feedback !== "") {
      setStartTimer(false);
      startFunc(ind, false);
    } else {
      setNextFunc(() => {
        setStartTimer(false);
        startFunc(ind, false);
      });
      setshowNextConfirmation(true);
    }
  };

  const synthesizeSpeech = async (text: string): Promise<void> => {
    // avoid overlay of audio
    if (state.canStop) {
      stopAudio();
    }
    if (state.userAudio) {
      const tokenObj = await getTokenOrRefresh();
      if (
        !state.abortController.signal.aborted &&
        tokenObj.authToken &&
        tokenObj.region
      ) {
        const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(
          tokenObj.authToken,
          tokenObj.region
        );

        speechConfig.speechSynthesisLanguage = "en-US";
        speechConfig.speechSynthesisVoiceName = "en-US-MichelleNeural";

        const speechSynthesizer = new speechsdk.SpeechSynthesizer(
          speechConfig,
          AudioConfig.fromStreamOutput(
            speechsdk.AudioOutputStream.createPullStream()
          )
        );
        setcanStop(true);
        return speechSynthesizer.speakTextAsync(
          text === ""
            ? state.interviewer.replace(/<br\/>/g, "")
            : text.replace(/<br\/>/g, "").replace(/\\/g, ""),
          (result: any) => {
            if (result) {
              speechSynthesizer.close();
              const blob = new Blob([result.audioData], { type: "audio/wav" });
              const blobUrl = URL.createObjectURL(blob);
              if (audioElement) {
                audioElement.src = blobUrl;
                audioElement.play();
              }
              return result.audioData;
            }
          },
          (error: string) => {
            setcanStop(false);
            console.log(error);
            speechSynthesizer.close();
          }
        );
      }
    }
  };

  useEffect(() => {
    const handleSessionStopped = async () => {
      if (sessionStopped && !micListening) {
        let resp:string|null = null;
        resp = await gpt(token); 
        if (!state.abortController.signal.aborted && resp !== null) {
          if (rsp !== "") {
            synthesizeSpeech(resp);
          }
        }
        setprocessing(false);
        setStartTimer(true);
      }
    };
    if (sessionStopped && !micListening) {
      setStartTimer(false);
    }
    handleSessionStopped();
  }, [sessionStopped, micListening]);

  useEffect(() => {
    if (state.hitMaxLen && micListening) {
      setErrorText(
        "You are nearing the maximum word limit for the interview. Auto muting your mic in 20 seconds."
      );
      setshowApiError(true);
      micTimeout = setTimeout(() => stopMic(), 20000);
      return () => {
        if (micTimeout) {
          clearTimeout(micTimeout);
        }
      };
    }
  }, [state.hitMaxLen]);

  useEffect(() => {
    return () => {
      // Cleanup code (runs when the component unmounts or before it re-runs)
      if (recognizer) {
        recognizer.stopContinuousRecognitionAsync();
      }
    };
  }, [recognizer]);

  useEffect(() => {
    const fetchData = async () => {
      if (state.initialLoad) {
        window.location.href = "/";
      } else {
        const tokenObj = await getTokenOrRefresh();
        if (tokenObj.authToken && tokenObj.region) {
          const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(
            tokenObj.authToken,
            tokenObj.region
          );
          speechConfig.speechRecognitionLanguage = "en-US";

          const audioConfig =
            speechsdk.AudioConfig.fromDefaultMicrophoneInput();
          setrecognizer(
            new speechsdk.SpeechRecognizer(speechConfig, audioConfig)
          );
        }
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (state.GPTCallCount === MAXCALLS - 1) {
      setErrorText("You have 1 more correspondence left.");
      setshowApiError(true);
    }
  }, [state.GPTCallCount]);

  useEffect(() => {
    if (!startTimer) {
      clearInterval(intInterval);
    } else {
      let interval1 = setInterval(() => {
        setTimer((prevTime) => prevTime + 1); // Increment time
      }, 1000);

      setIntInterval(interval1);
    }
    return () => clearInterval(intInterval);
  }, [startTimer]);

  useEffect(() => {
    updatesession({ time: timer });
  }, [timer]);

  useEffect(() => {
    if (micActivationTimeout !== null) {
      setmicListening(false);
    }
  }, [micActivationTimeout]);

  useEffect(() => {
    if (state.session.length > 0 && state.currentQuestion >= 0) {
      setTimer(state.retryPast ? 0 : state.session[state.currentQuestion].time);
    }
    if(state.isAssessment)
    {
      setStartTimer(true);
    }
  }, [state.currentQuestion]);

  useEffect(() => {
    const setupSpeechRecognition = async () => {
      if (micListening) {
        if (!state.abortController.signal.aborted && recognizer) {
          recognizer.startContinuousRecognitionAsync();
          setmicActivationTimeout(null);

          recognizer.canceled = (s, e) => {
            setErrorText("Mic activation failed. DO NOT REFRESH PAGE. Retrying in 7 seconds.");
            if(!updatedAssessmentMicActivationFailure && token !== "")
            {
              UpdateAssessmentMicActivationFailure(token, state.currentQuestion, state.session);
              setUpdatedAssessmentMicActivationFailure(true);
            }

            setmicListening(false);
            setshowApiError(true);
            setmicActivationTimeout(
              setTimeout(() => {
                setmicListening(true);
                setErrorText(
                  "Encountered a problem reaching the api, retrying in 3 seconds..."
                );
                setshowApiError(false);
              }, 7000)
            );
          };
          recognizer.recognized = (s: any, e: any) => {
            // Prevent updating rsp if not fullscreen and mic is active
            if ((state.isAssessment) && !isFullscreen && micListening) return;
            if (e.result.reason === speechsdk.ResultReason.RecognizedSpeech) {
              setrsp((prev: string) => `${prev} ${e.result.text}`);
            }
          };
          recognizer.recognizing = (s: any, e: any) => {
            // Prevent updating rspLive if not fullscreen and mic is active
            if ((state.isAssessment) && !isFullscreen && micListening) return;
            setrspLive(e.result.text);
            setUpdatedAssessmentMicActivationFailure(false);
          };
          recognizer.sessionStopped = async (s: any, e: any) => {
            setsessionStopped(true);
          };
        }
      } else if (micActivationTimeout == null) {
        setspeechLen(state.contextLength);
        //sethitMaxLen(false);
        setshowApiError(false);
        setErrorText(
          "Encountered a problem reaching the api, retrying in 3 seconds..."
        );
      }
    };
    setupSpeechRecognition();
  }, [micListening, isFullscreen]);




// ========================================================================================
const getSessionId = () => {
  if (state.isAssessment && token) {
    return token; // Use assessment token for assessments
  }
  // for testing without assessmentlink token
  return state.session?.[state.currentQuestion]?.sessionId || 
         state.session?.[0]?.sessionId || 
         'no-session';
};

// Session monitoring fields in redis are updated here and sent every 10s
const monitoringDataRef = useRef({
  windowEvents: { blurCount: 0, focusCount: 0, maxDuration: 0 },
  cursorEvents: { leftCount: 0, reenterCount: 0, maxDuration: 0 },
  resizeEvents: { 
    count: 0, 
    maxSize: { width: 0, height: 0 }, 
    minSize: { width: Infinity, height: Infinity } 
  },
  copyCount: 0, 
  externalPastes: { count: 0, texts: [] as string[] },
  devtoolsEvents: { openCount: 0, closeCount: 0, maxDuration: 0 },
  fullscreenEvents: { exitCount: 0, reenterCount: 0, maxDuration: 0 }, // fullscreen field
});

const [monitoringDataChanged, setMonitoringDataChanged] = useState(false);


// Reset monitoring data when question changes since backend logging is per question 
useEffect(() => {
    if (state.isAssessment) {
      monitoringDataRef.current = {
        windowEvents: { blurCount: 0, focusCount: 0, maxDuration: 0 },
        cursorEvents: { leftCount: 0, reenterCount: 0, maxDuration: 0 },
        resizeEvents: { 
          count: 0, 
          maxSize: { width: 0, height: 0 }, 
          minSize: { width: Infinity, height: Infinity } 
        },
        copyCount: 0,
        externalPastes: { count: 0, texts: [] },
        devtoolsEvents: { openCount: 0, closeCount: 0, maxDuration: 0 },
        fullscreenEvents: { exitCount: 0, reenterCount: 0, maxDuration: 0 },
      };
      setMonitoringDataChanged(false);
    }
  }, [state.currentQuestion, state.isAssessment]);
  

// Periodic sync of monitoring data to backend -- every 10s
useEffect(() => {
  const interval = setInterval(() => {
    if (monitoringDataChanged && state.isAssessment) {
      logAssessmentEvent(getSessionId(), 'session_monitoring_update', 
        {questionIndex: state.currentQuestion, ...monitoringDataRef.current });
      
      // Reset to zeros immediately after sending
      monitoringDataRef.current = {
        windowEvents: { blurCount: 0, focusCount: 0, maxDuration: 0 },
        cursorEvents: { leftCount: 0, reenterCount: 0, maxDuration: 0 },
        resizeEvents: {
          count: 0,
          maxSize: { width: 0, height: 0 },
          minSize: { width: Infinity, height: Infinity }
        },
        copyCount: 0,
        externalPastes: { count: 0, texts: [] },
        devtoolsEvents: { openCount: 0, closeCount: 0, maxDuration: 0 },
        fullscreenEvents: { exitCount: 0, reenterCount: 0, maxDuration: 0 },
      };
      setMonitoringDataChanged(false);
    }
  }, 10000); // sync every 10 seconds

  return () => clearInterval(interval);
}, [monitoringDataChanged, state.isAssessment]);

// ====================================== Copy/Paste ====================================================
// Log paste events as events of  external or internal sources
// TODO: Block copy of the question or not? Block paste of external or not?
 
// State to track last internal copy (either copy from whiteboard or question text)
const [lastInternalCopy, setLastInternalCopy] = useState<{text: string, origin: string}>({text: '', origin: ''});

  useEffect(() => { // Monitor copy/paste events for pastes of external source
    if (!state.isAssessment) return;
    
    const handleCopy = (e: ClipboardEvent) => { // increment copyCount on every copy event and set lastInternalCopy
      const target = e.target as HTMLElement;
      const copiedText = window.getSelection()?.toString() || '';
      
      let copyOrigin = 'external';
      
      // Check if copy is from whiteboard
      const isWhiteboardActive = state.useWhiteboard || document.querySelector('.pt-8');
      if (isWhiteboardActive && (target.tagName === 'BR' || target.closest('[class*="cm-"]'))) {
        copyOrigin = 'whiteboard';
      } else if (target.closest('.text-left p')) {
        copyOrigin = 'question';
      }
      
      // Store internal copies
      if (copyOrigin !== 'external') {
        setLastInternalCopy({text: copiedText, origin: copyOrigin});
      }
      
      monitoringDataRef.current.copyCount++;
      setMonitoringDataChanged(true);
    };

    const handlePaste = (e: ClipboardEvent) => { // update externalPastes if any paste event is external
      const pastedText = e.clipboardData?.getData('text') || '';
      const target = e.target as HTMLElement;
      
      let destination = 'other';
      let isExternalSource = true;

      // Check paste destination
      const isWhiteboardActive = state.useWhiteboard || document.querySelector('.pt-8');
      if (isWhiteboardActive && target.tagName === 'BR') {
        destination = 'whiteboard';
      } else if (target.closest('.text-left p')) {
        destination = 'question';
      } 

      // Check if paste matches last internal copy
      if (pastedText === lastInternalCopy.text && lastInternalCopy.text !== '') {
        isExternalSource = false;
      } 

      // Update session monitoring for external pastes
      if (isExternalSource) {
        monitoringDataRef.current.externalPastes.count++;
        monitoringDataRef.current.externalPastes.texts.push(pastedText);
        setMonitoringDataChanged(true);
      }
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [state.useWhiteboard, lastInternalCopy]);


  // ======================================== Dev Tools ===================================================
  const [devtoolsStartTime, setDevtoolsStartTime] = useState<number | null>(null);
  const [devtoolsOpen, setDevtoolsOpen] = useState(false);
  
  // Monitor window dims every 10s for devtools open/closed. 
  useEffect(() => { 
    if (!state.isAssessment) return;
    
    const checkDevtools = () => {
      const threshold = 160;
      const isOpen = window.outerWidth - window.innerWidth > threshold || 
                     window.outerHeight - window.innerHeight > threshold;
      
      if (isOpen && !devtoolsOpen) {
        setDevtoolsOpen(true);
        setDevtoolsStartTime(Date.now());
        monitoringDataRef.current.devtoolsEvents.openCount++;
        setMonitoringDataChanged(true);
      } else if (!isOpen && devtoolsOpen) {
        setDevtoolsOpen(false);
        const duration = devtoolsStartTime ? Date.now() - devtoolsStartTime : 0;
        monitoringDataRef.current.devtoolsEvents.closeCount++;
        monitoringDataRef.current.devtoolsEvents.maxDuration = Math.max(monitoringDataRef.current.devtoolsEvents.maxDuration, duration);
        setMonitoringDataChanged(true);
        setDevtoolsStartTime(null);
      }
    };
  
    const interval = setInterval(checkDevtools, 2000); // every 2s
    return () => clearInterval(interval);
  }, [devtoolsOpen, devtoolsStartTime, state.isAssessment]);


  // =========================================== Mouse Tracking (viewport) =======================================================
  // Listen for mouseleave/enter events on the document and log left/reenterCounts and maxDuration
  
  const [cursorLeftTime, setCursorLeftTime] = useState<number | null>(null);
  useEffect(() => {
    if (!state.isAssessment) return;
    
    let leaveTimeout: NodeJS.Timeout | null = null;
    
    const handleMouseLeave = () => {
      leaveTimeout = setTimeout(() => { // brief timeout for accidental overshoot on screenedges. Previously for address/menu/tab bar handling
        setCursorLeftTime(Date.now());
        monitoringDataRef.current.cursorEvents.leftCount++;
        setMonitoringDataChanged(true);
      }, 500); // 500ms
    };
    
    const handleMouseEnter = () => {
      if (leaveTimeout) {
        clearTimeout(leaveTimeout);
        leaveTimeout = null;
        return;
      }
      
      if (cursorLeftTime) {
        const duration = Date.now() - cursorLeftTime;
        monitoringDataRef.current.cursorEvents.reenterCount++;
        monitoringDataRef.current.cursorEvents.maxDuration = Math.max(monitoringDataRef.current.cursorEvents.maxDuration, duration);
        setMonitoringDataChanged(true);
        setCursorLeftTime(null);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      if (leaveTimeout) clearTimeout(leaveTimeout);
    };
  }, [cursorLeftTime, state.isAssessment]);
    


  // ==============  window blur/focus Visibility change ==========================
  // Listen for window blur/focus 
  const [blurStartTime, setBlurStartTime] = useState<number | null>(null);

  useEffect(() => {
    if (!state.isAssessment) return;

      // Update local state on events
      const handleBlur = () => {
        setBlurStartTime(Date.now());
        monitoringDataRef.current.windowEvents.blurCount++;
        setMonitoringDataChanged(true);
      };
     
      const handleFocus = () => {
        if (blurStartTime) {
          const duration = Date.now() - blurStartTime;
          monitoringDataRef.current.windowEvents.focusCount++;
          monitoringDataRef.current.windowEvents.maxDuration = Math.max(monitoringDataRef.current.windowEvents.maxDuration, duration);
          setMonitoringDataChanged(true);
          setBlurStartTime(null);
        }
      };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
  
    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [blurStartTime, state.isAssessment]);
 


  // ================================ Window resize ====================================================
  // listen for window resize events and update event count, max/min sizes
  useEffect(() => {
    if (!state.isAssessment) return; // Only in assessment

    let resizeTimeout: NodeJS.Timeout; // Use timeout(1s) to skip intermediate sizes in ongoing resizing
 
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Fullscreen events count into resize. Both fullscreen exit/reentry are counted as resize events ... these are accounted for in the backend calculateCheatingScore
        // This here handles fullscreenEvent bouncing. And still in the backend we account for fullscreen, OR could use 2000ms here and not do backend 
        const now = Date.now();
        if (now - lastFullscreenEventTimeRef.current < 500) return; 
    
        const { innerWidth: width, innerHeight: height } = window;
        
        monitoringDataRef.current.resizeEvents.count++;
        monitoringDataRef.current.resizeEvents.maxSize.width = Math.max(monitoringDataRef.current.resizeEvents.maxSize.width, width);
        monitoringDataRef.current.resizeEvents.maxSize.height = Math.max(monitoringDataRef.current.resizeEvents.maxSize.height, height);
        monitoringDataRef.current.resizeEvents.minSize.width = Math.min(monitoringDataRef.current.resizeEvents.minSize.width, width);
        monitoringDataRef.current.resizeEvents.minSize.height = Math.min(monitoringDataRef.current.resizeEvents.minSize.height, height);
        setMonitoringDataChanged(true);
      }, 1500);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [state.isAssessment]);


  // ============================= Full screen ==================================
  const fullscreenBtnRef = useRef<HTMLButtonElement>(null);
  const lastFullscreenEventTimeRef = useRef<number>(0);

  // Request fullscreen on mount, only if assessment or practice
  useEffect(() => {
    if (!(state.isAssessment || state.isPractice)) return;

    const requestFullscreen = () => {
      const el = document.documentElement;
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if ((el as any).webkitRequestFullscreen) {
        (el as any).webkitRequestFullscreen();
      } else if ((el as any).msRequestFullscreen) {
        (el as any).msRequestFullscreen();
      }
    };

    // Only request fullscreen if not already in fullscreen
    if (!document.fullscreenElement && !(document as any).webkitFullscreenElement && !(document as any).msFullscreenElement) {
      requestFullscreen();
    }

      const handleFullscreenChange = () => {
        // Handle bouncing of fullscreen events
        const now = Date.now();
        if (now - lastFullscreenEventTimeRef.current < 500) return; // Skip if within 100ms
        lastFullscreenEventTimeRef.current = now; // Immediate sync update
      
      const nowFullscreen = 
        !!(document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).msFullscreenElement);
      setIsFullscreen(nowFullscreen);
        

      if (!nowFullscreen) {
        // Just exited fullscreen
        setFullscreenExitTime(Date.now());
        monitoringDataRef.current.fullscreenEvents.exitCount++;
        setMonitoringDataChanged(true);
      } else { 
        // Just re-entered fullscreen
        if (fullscreenExitTime) {
          const duration = Date.now() - fullscreenExitTime;
          monitoringDataRef.current.fullscreenEvents.reenterCount++;
          monitoringDataRef.current.fullscreenEvents.maxDuration = Math.max(monitoringDataRef.current.fullscreenEvents.maxDuration, duration);
          setMonitoringDataChanged(true);
          setFullscreenReentryDuration(duration);
          setFullscreenExitTime(null)
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
    };
  // Add fullscreenExitTime to dependency so it is up-to-date
  }, [state.isAssessment, state.isPractice, fullscreenExitTime]); // Include fullscreenExitTime!

  // Handler for re-enabling fullscreen
  const handleEnterFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if ((el as any).webkitRequestFullscreen) {
      (el as any).webkitRequestFullscreen();
    } else if ((el as any).msRequestFullscreen) {
      (el as any).msRequestFullscreen();
    }
  };


  // Optionally, you can use fullscreenReentryDuration elsewhere in your component
  // For example, to log or display it for debugging:
  // console.log('Time to re-enter fullscreen (ms):', fullscreenReentryDuration);

  if (showLoader) return <FeedbackLoader />;
  return (
    <>
      {/* Fullscreen overlay */}
      {(state.isAssessment || state.isPractice) && !isFullscreen && (
        <div
          style={{
            position: "fixed",
            zIndex: 9999,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(128,128,128,0.7)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <button
            ref={fullscreenBtnRef}
            onClick={handleEnterFullscreen}
            style={{
              padding: "16px 32px",
              fontSize: "20px",
              borderRadius: "8px",
              border: "none",
              background: "#333",
              color: "#fff",
              cursor: "pointer"
            }}
          >
            Re-enter Fullscreen
          </button>
          <div style={{ color: "red", marginTop: 24, fontSize: 18 }}>
            Please re-enter fullscreen to continue.
          </div>
        </div>
      )}
      <Nav />
      <div className="relative">
        {/* Light gray overlay for practice mode */}
        {state.isPractice && (
          <div className="absolute inset-0 bg-gray-200 bg-opacity-50 z-40 pointer-events-none"></div>
        )}
        <div className="overflow-hidden bg-white relative z-50">
          <ResizeFeedback
            timerOn={startTimer}
            displayPastFeedback={state.displayPastFeedback}
            retryClick={
              !state.displayPastFeedback
                ? () => {
                    setisRetry(true);
                    startFunc(state.currentQuestion, true);
                  }
                : () => {
                    navigateThenRun(() => {
                      setrole(state.session[0].role);
                      updatesession({
                        sessionId: generateGUID(),
                        type: state.session[state.currentQuestion].question.type,
                      });
                      settype(state.session[state.currentQuestion].question.type);
                      setQs(null, state.session[state.currentQuestion].question);
                      setqType("choose question");
                      setcurrentQuestion(0);
                      setretryPast(true);
                    }, "/interview");
                  }
            }
            nextClick={
              state.currentQuestion < state.session.length - 1
                ? () => startFunc(state.currentQuestion + 1, false, token)
                : undefined
            }
            org={{
              inOrg:
                state.profileUser &&
                state.profileUser.organization !== undefined &&
                state.profileUser.organization !== ""
                  ? true
                  : false,
              accessibleToOrg: state.pastInfo["orgAccessible"],
              updateAccessibleToOrg: (e) => updateAccessibleToOrg(e),
            }}
            adminView={isAdminView}
            micOpts={{
              stopMic,
              sttFromMic,
              micListening,
              processing: state.processing,
            }}
            synthesizeSpeech={synthesizeSpeech}
            setShowLoader={setShowLoader}
            recognizer={recognizer}
            newQuestionClick={(ind: number) => NewQuestionClick(ind)}
          />
        </div>
      </div>
    </>
  );
}

export default InterviewScreen;
